/**
 * src/pages/Cart.jsx
 * Shipping is not calculated/charged anywhere on this site right now —
 * total is subtotal minus any coupon discount, full stop.
 *
 * "Buy it now" on the product page navigates here with
 * { state: { openCheckout: true } }, which auto-opens the checkout sheet
 * immediately on load — the person never sees the plain cart list first.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useCart } from "../lib/cart";
import { formatINR } from "../lib/supabaseProducts";
import { supabase } from "../supabaseClient";

function CheckoutSheet({ subtotal, onClose, onPlaced }) {
  const [form, setForm] = useState({ name: "", phone: "", address_line1: "", address_line2: "", city: "Gurgaon", state: "Haryana", pincode: "" });
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponEligible, setCouponEligible] = useState(true); // optimistic until we actually check
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");
  const { lines, clear } = useCart();

  async function checkEligibility(phone) {
    if (!phone || phone.trim().length < 10) return;
    setCheckingEligibility(true);
    const { data, error } = await supabase.rpc("is_first_time_customer", { p_phone: phone.trim() });
    setCheckingEligibility(false);
    if (error) return; // fail open — don't block checkout over a network hiccup, server still enforces the real rule
    setCouponEligible(data);
    if (!data) setCouponApplied(false); // was toggled on before we knew — turn it back off, don't show a false discount
  }

  const estimatedDiscount = couponApplied && couponEligible ? Math.round(subtotal * 0.05) : 0;
  const estimatedTotal = subtotal - estimatedDiscount;

  async function handlePlaceOrder() {
    setError("");
    if (!form.name || !form.phone || !form.address_line1 || !form.pincode) {
      setError("Please fill in your name, phone, address and pincode.");
      return;
    }
    setPlacing(true);

    const { data, error: rpcError } = await supabase.rpc("place_online_order", {
      p_customer_phone: form.phone,
      p_customer_name: form.name,
      p_address: {
        full_name: form.name,
        phone: form.phone,
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        state: form.state,
        pincode: form.pincode,
      },
      p_items: lines.map((l) => ({ stock_id: l.stockId, quantity: l.qty })),
      p_coupon_code: couponApplied ? "FIRST5" : null,
    });

    await new Promise((r) => setTimeout(r, 900));
    setPlacing(false);

    if (rpcError) {
      setError(rpcError.message || "Something went wrong placing your order. Please try again or WhatsApp us.");
      return;
    }

    clear();
    if (couponApplied && !data.coupon_applied) {
      // Estimate assumed it would apply, but the server correctly rejected
      // it (e.g. this phone has already used it before) — the actual
      // charged amount in `data` is already correct, this is just making
      // sure the person isn't confused about why it's higher than expected.
      window.alert("Note: the FIRST5 discount only applies to your very first order and wasn't applied this time. Your order has still been placed at the full price shown below.");
    }
    onPlaced(data);
  }

  return (
    <div className="checkout-backdrop" onClick={onClose}>
      <div className="checkout-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="checkout-sheet-handle" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>🔒</span>
            <h2 style={{ fontSize: "1.125rem", fontWeight: 700, margin: 0 }}>Secure Checkout</h2>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
          {[
            { key: "name", placeholder: "Full name" },
            { key: "phone", placeholder: "Phone number" },
            { key: "address_line1", placeholder: "Address line 1" },
            { key: "address_line2", placeholder: "Address line 2 (optional)" },
            { key: "city", placeholder: "City" },
            { key: "pincode", placeholder: "Pincode" },
          ].map((f) => (
            <input
              key={f.key}
              value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              onBlur={f.key === "phone" ? (e) => checkEligibility(e.target.value) : undefined}
              placeholder={f.placeholder}
              className="checkout-input"
            />
          ))}
        </div>

        <div className="checkout-coupon">
          <div>
            <p style={{ fontWeight: 600, margin: 0, fontSize: 14 }}>🎁 FIRST5 — 5% off your first order</p>
            {!couponEligible ? (
              <p style={{ fontSize: 12, margin: "2px 0 0", color: "var(--destructive)" }}>
                This number has already used a first-order discount
              </p>
            ) : (
              <p className="muted" style={{ fontSize: 12, margin: "2px 0 0" }}>
                Enter your phone number above to check eligibility
              </p>
            )}
          </div>
          <button
            className={`btn btn-sm ${couponApplied ? "btn-primary" : "btn-outline"}`}
            disabled={!couponEligible || checkingEligibility}
            onClick={() => setCouponApplied((v) => !v)}
          >
            {checkingEligibility ? "Checking…" : couponApplied ? "Applied ✓" : "Apply"}
          </button>
        </div>

        <div className="checkout-bill">
          <div className="checkout-bill-row">
            <span className="muted">Subtotal</span>
            <span>{formatINR(subtotal)}</span>
          </div>
          {couponApplied && (
            <div className="checkout-bill-row" style={{ color: "var(--primary)" }}>
              <span>FIRST5 discount</span>
              <span>−{formatINR(estimatedDiscount)}</span>
            </div>
          )}
          <div className="checkout-bill-row checkout-bill-total">
            <span>Total</span>
            <span>{formatINR(estimatedTotal)}</span>
          </div>
        </div>

        {error && <p style={{ color: "var(--destructive)", fontSize: 13, marginTop: 10 }}>{error}</p>}

        <button className="checkout-pay-btn" disabled={placing} onClick={handlePlaceOrder}>
          {placing ? (
            <span className="checkout-spinner" />
          ) : (
            <>
              <span>⚡ Pay Now</span>
              <span className="checkout-pay-amount">{formatINR(estimatedTotal)}</span>
            </>
          )}
        </button>
        <p className="muted mt-2" style={{ fontSize: 12, textAlign: "center" }}>
          🔒 we'll call within a few hours to confirm before packing.
        </p>
      </div>
    </div>
  );
}

export default function Cart() {
  const { lines, setQty, remove, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCheckout, setShowCheckout] = useState(false);

  // "Buy it now" lands here with { state: { openCheckout: true } } —
  // open the sheet immediately instead of showing the plain cart first.
  useEffect(() => {
    if (location.state?.openCheckout && lines.length > 0) {
      setShowCheckout(true);
    }
  }, [location.state, lines.length]);

  function handlePlaced(orderResult) {
    setShowCheckout(false);
    navigate("/order-confirmed", { state: orderResult });
  }

  if (lines.length === 0) {
    return (
      <div className="container section text-center">
        <Helmet>
          <title>Your Cart | Ritu Fashion Store</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1 style={{ fontSize: "1.75rem", fontWeight: 800 }}>Your bag is empty</h1>
        <p className="muted mt-2">Little outfits are waiting to be discovered.</p>
        <Link to="/shop" className="btn btn-primary mt-8">Start shopping</Link>
      </div>
    );
  }

  return (
    <div className="container section">
      <Helmet>
        <title>Your Cart | Ritu Fashion Store</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <h1 style={{ fontSize: "1.75rem", fontWeight: 800 }}>Your bag</h1>

      <div style={{ marginTop: "2rem" }} className="cart-layout">
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {lines.map((line) => (
            <li key={line.stockId} style={{ display: "flex", gap: 16, borderBottom: "1px solid var(--border)", paddingBottom: 24, marginBottom: 24 }}>
              {line.image ? (
                <img src={line.image} alt={line.name} width={96} height={112} style={{ width: 96, height: 112, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
              ) : (
                <div style={{ width: 96, height: 112, borderRadius: 10, background: "var(--muted)", flexShrink: 0 }} />
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <Link to={`/product/${line.slug}`} style={{ fontWeight: 600 }}>{line.name}</Link>
                <p className="muted mt-2" style={{ fontSize: 14 }}>Size {line.sizeLabel}</p>
                <p style={{ fontWeight: 600, fontSize: 14, marginTop: 4 }}>{formatINR(line.price)}</p>

                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--border)", borderRadius: 999 }}>
                    <button aria-label="Decrease" onClick={() => setQty(line.stockId, line.qty - 1)} style={{ width: 32, height: 32, border: "none", background: "none", cursor: "pointer" }}>−</button>
                    <span style={{ width: 28, textAlign: "center", fontSize: 14, fontWeight: 600 }}>{line.qty}</span>
                    <button
                      aria-label="Increase"
                      onClick={() => setQty(line.stockId, line.qty + 1)}
                      disabled={line.qty >= line.maxQty}
                      style={{ width: 32, height: 32, border: "none", background: "none", cursor: line.qty >= line.maxQty ? "not-allowed" : "pointer", opacity: line.qty >= line.maxQty ? 0.4 : 1 }}
                    >
                      +
                    </button>
                  </div>
                  <button onClick={() => remove(line.stockId)} className="muted" style={{ fontSize: 13, border: "none", background: "none", cursor: "pointer" }}>
                    🗑 Remove
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>

        <aside className="card" style={{ padding: 24, height: "fit-content", background: "var(--secondary)" }}>
          <h2 style={{ fontSize: "1.125rem", fontWeight: 700 }}>Order summary</h2>
          <div style={{ marginTop: 18, fontSize: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid var(--border)", paddingTop: 10, fontSize: 16 }}>
              <span style={{ fontWeight: 700 }}>Total</span>
              <span style={{ fontWeight: 700 }}>{formatINR(subtotal)}</span>
            </div>
          </div>

          <button className="checkout-pay-btn mt-6" onClick={() => setShowCheckout(true)}>
            <span>⚡ Fast Checkout</span>
            <span className="checkout-pay-amount">{formatINR(subtotal)}</span>
          </button>

          <button onClick={clear} className="muted mt-4" style={{ width: "100%", textAlign: "center", fontSize: 12, border: "none", background: "none", cursor: "pointer" }}>
            Clear bag
          </button>
        </aside>
      </div>

      {showCheckout && (
        <CheckoutSheet subtotal={subtotal} onClose={() => setShowCheckout(false)} onPlaced={handlePlaced} />
      )}
    </div>
  );
}